import COOKIES from '@/constants/cookies';
import { useEffect, useState } from 'react';
import { useAppSelector } from '.';
import { useConfidenceClient } from './useConfidenceExperiment';

/**
 * Custom hook to get Confidence flags with SSR support
 * @param flagKey - The feature flag key to resolve
 * @param defaultValue - Default value to use while loading or if flag fails
 * @returns The resolved flag value
 */
export function useConfidenceFlag<T = any>(flagKey: string, defaultValue: T, shouldFetch: boolean = true): T {
  const confidence = useConfidenceClient();

  // Get SSR-preloaded flags from Redux store
  const ssrFlags = useAppSelector((state) => state.flags);
  const ssrFlagValue = ssrFlags?.[flagKey]?.payload;
  const cookiesState = useAppSelector((state) => state.cookies) || {};
  const acceptedAnalytics = cookiesState[COOKIES.BOKADIREKT_COOKIE_PREFERENCES_MP] === 'Analytics';

  const hasSSRValue = ssrFlagValue !== undefined;

  // Initialize with SSR value if available, otherwise use default
  const initialValue = hasSSRValue ? ssrFlagValue : defaultValue;
  const [flagValue, setFlagValue] = useState<T>(initialValue as T);

  useEffect(() => {
    if (!shouldFetch || !acceptedAnalytics || !confidence?.isReady || !confidence?.client) {
      return;
    }

    const fetchFlag = async () => {
      try {
        const value = await confidence.client.getFlag(flagKey, defaultValue as any);
        setFlagValue(value as T);
      } catch (error) {
        console.warn(`Failed to fetch Confidence flag: ${flagKey}`, error);
      }
    };

    fetchFlag();
  }, [shouldFetch, acceptedAnalytics, confidence?.isReady, confidence?.client]);

  return flagValue;
}
